import { createContext, useContext, useEffect, useState } from "react";
import { checkEmailVerified } from "../../services/authService";
import { getGroups } from "../../services/groupService";
import { AuthContext } from "../Auth/AuthContext";

const GroupContext = createContext();

export const useGroupContext = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error(
      "useGroupContext must be used within a GroupContextProvider",
    );
  }
  return context;
};

export const GroupContextProvider = ({ children }) => {
  const [currentContext, setCurrentContext] = useState("personal");
  const [currentGroupId, setCurrentGroupId] = useState(null);
  const [currentGroupName, setCurrentGroupName] = useState("");
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  const { isLoggedIn, user } = useContext(AuthContext);

  useEffect(() => {
    if (isLoggedIn !== undefined) {
      setAuthReady(true);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (authReady) {
      if (isLoggedIn && user) {
        loadSavedContextForUser();
        loadUserGroups();
      } else {
        resetToPersonalContext();
      }
    }
  }, [authReady, isLoggedIn, user?.uid]);

  const resetToPersonalContext = () => {
    setCurrentContext("personal");
    setCurrentGroupId(null);
    setCurrentGroupName("");
    setUserGroups([]);
    setLoading(false);

    localStorage.removeItem("selectedContext");
    localStorage.removeItem("selectedGroupId");
    localStorage.removeItem("selectedGroupName");
  };

  const loadSavedContextForUser = () => {
    const savedContext = localStorage.getItem("selectedContext");
    const savedGroupId = localStorage.getItem("selectedGroupId");
    const savedGroupName = localStorage.getItem("selectedGroupName");

    if (savedContext && savedContext === "group" && savedGroupId) {
      setCurrentContext("group");
      setCurrentGroupId(parseInt(savedGroupId));
      setCurrentGroupName(savedGroupName || "");
    } else {
      setCurrentContext("personal");
      setCurrentGroupId(null);
      setCurrentGroupName("");
    }
  };

  const loadUserGroups = async () => {
    try {
      setLoading(true);
      if (!checkEmailVerified()) {
        console.log("Email not verified, skipping groups loading");
        setUserGroups([]);
        setLoading(false);
        return;
      }
      //console.log('Loading groups...');
      const groups = await getGroups();
      //console.log('Groups loaded:', groups);
      setUserGroups(groups);

      const savedGroupId = localStorage.getItem("selectedGroupId");
      if (savedGroupId) {
        const groupExists = groups.find((g) => g.id === parseInt(savedGroupId));
        if (!groupExists) {
          console.log(
            "Saved group no longer accessible, switching to personal",
          );
          switchToPersonal();
        }
      }
    } catch (error) {
      console.error("Error loading groups:", error);
      setUserGroups([]);

      if (error.response?.status === 401) {
        console.log("Unauthorized - email may not be verified");
        setUserGroups([]);
      } else if (error.response?.status === 403) {
        switchToPersonal();
      }
    } finally {
      setLoading(false);
    }
  };

  const switchToPersonal = () => {
    setCurrentContext("personal");
    setCurrentGroupId(null);
    setCurrentGroupName("");
    localStorage.setItem("selectedContext", "personal");
    localStorage.removeItem("selectedGroupId");
    localStorage.removeItem("selectedGroupName");
  };

  const switchToGroup = (groupId, groupName) => {
    setCurrentContext("group");
    setCurrentGroupId(groupId);
    setCurrentGroupName(groupName);
    localStorage.setItem("selectedContext", "group");
    localStorage.setItem("selectedGroupId", groupId.toString());
    localStorage.setItem("selectedGroupName", groupName);
  };

  const getContextParams = () => {
    if (currentContext === "group" && currentGroupId) {
      return {
        context: "group",
        groupId: currentGroupId,
      };
    }
    return {
      context: "personal",
    };
  };

  const value = {
    currentContext,
    currentGroupId,
    currentGroupName,
    userGroups,
    loading: loading || !authReady,
    authReady,

    switchToPersonal,
    switchToGroup,
    loadUserGroups,

    getContextParams,

    isPersonalMode: currentContext === "personal",
    isGroupMode: currentContext === "group",
    hasGroups: userGroups.length > 0,
    currentGroup: userGroups.find((g) => g.id === currentGroupId) || null,
  };

  return (
    <GroupContext.Provider value={value}>{children}</GroupContext.Provider>
  );
};
